import { Drawer, FocusTrap } from '@mantine/core';
import { useCallback, useEffect, useRef, useState, type PointerEvent, type PropsWithChildren } from 'react';

import classes from './modal.module.css';

type BottomSheetProps = PropsWithChildren<{
  opened: boolean;
  onClose: () => void;
  onExitTransitionEnd?: () => void;
  title: string;
  closeLabel: string;
  contentSized?: boolean;
  trapFocus?: boolean;
  closeOnEscape?: boolean;
  zIndex?: number;
}>;

const SWIPE_CLOSE_THRESHOLD = 88;
const SWIPE_START_THRESHOLD = 10;

type TouchGesture = {
  x: number;
  y: number;
  fromTop: boolean;
  canDrag: boolean;
  active: boolean;
};

export const BottomSheet = ({
  opened,
  onClose,
  onExitTransitionEnd,
  title,
  closeLabel,
  contentSized,
  trapFocus = true,
  closeOnEscape = true,
  zIndex,
  children,
}: BottomSheetProps) => {
  const startY = useRef<number | null>(null);
  const offsetRef = useRef(0);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const touchGesture = useRef<TouchGesture | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const resetDrag = useCallback(() => {
    startY.current = null;
    offsetRef.current = 0;
    setDragOffset(0);
    setDragging(false);
  }, []);

  const handleClose = useCallback(() => {
    touchGesture.current = null;
    resetDrag();
    onClose();
  }, [onClose, resetDrag]);

  const updateDrag = useCallback((clientY: number) => {
    if (startY.current === null) return;
    const nextOffset = Math.max(0, clientY - startY.current);
    offsetRef.current = nextOffset;
    setDragOffset(nextOffset);
  }, []);

  const finishDrag = useCallback(() => {
    if (startY.current === null) return;
    const shouldClose = offsetRef.current >= SWIPE_CLOSE_THRESHOLD;
    if (shouldClose) handleClose();
    else resetDrag();
  }, [handleClose, resetDrag]);

  useEffect(() => {
    const content = contentElement;
    if (!opened || !content) return;

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        touchGesture.current = null;
        resetDrag();
        return;
      }

      const touch = event.touches[0];
      if (!touch) return;
      const fromTop = event.target instanceof Element && Boolean(event.target.closest(`.${classes.sheetTop}`));
      touchGesture.current = {
        x: touch.clientX,
        y: touch.clientY,
        fromTop,
        canDrag: fromTop || (bodyRef.current?.scrollTop ?? 0) <= 0,
        active: false,
      };
    };

    const handleTouchMove = (event: TouchEvent) => {
      const gesture = touchGesture.current;
      const touch = event.touches[0];
      if (event.touches.length !== 1) {
        touchGesture.current = null;
        resetDrag();
        return;
      }
      if (!gesture || !touch) return;

      if (!gesture.active) {
        if (!gesture.canDrag || (!gesture.fromTop && (bodyRef.current?.scrollTop ?? 0) > 0)) return;
        const deltaX = touch.clientX - gesture.x;
        const deltaY = touch.clientY - gesture.y;
        if (Math.abs(deltaY) < SWIPE_START_THRESHOLD && Math.abs(deltaX) < SWIPE_START_THRESHOLD) return;
        if (deltaY <= 0 || Math.abs(deltaY) <= Math.abs(deltaX)) {
          touchGesture.current = null;
          return;
        }

        gesture.active = true;
        startY.current = gesture.y;
        setDragging(true);
      }

      event.preventDefault();
      updateDrag(touch.clientY);
    };

    const handleTouchEnd = () => {
      if (touchGesture.current?.active) finishDrag();
      touchGesture.current = null;
    };

    const handleTouchCancel = () => {
      touchGesture.current = null;
      resetDrag();
    };

    content.addEventListener('touchstart', handleTouchStart, { passive: true });
    content.addEventListener('touchmove', handleTouchMove, { passive: false });
    content.addEventListener('touchend', handleTouchEnd);
    content.addEventListener('touchcancel', handleTouchCancel);

    return () => {
      content.removeEventListener('touchstart', handleTouchStart);
      content.removeEventListener('touchmove', handleTouchMove);
      content.removeEventListener('touchend', handleTouchEnd);
      content.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [contentElement, finishDrag, opened, resetDrag, updateDrag]);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button')) return;

    startY.current = event.clientY;
    offsetRef.current = 0;
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') updateDrag(event.clientY);
  };

  return (
    <Drawer.Root
      opened={opened}
      onClose={handleClose}
      onExitTransitionEnd={onExitTransitionEnd}
      trapFocus={trapFocus}
      closeOnEscape={closeOnEscape}
      zIndex={zIndex}
      position="bottom"
      size="auto"
      padding={0}
      classNames={{ content: `${classes.sheetContent} ${dragging ? classes.sheetDragging : ''}` }}
    >
      <Drawer.Overlay className={classes.overlay} />
      <Drawer.Content
        ref={setContentElement}
        data-content-sized={contentSized || undefined}
        style={{ '--sheet-offset': `${dragOffset}px` }}
      >
        <FocusTrap.InitialFocus />
        <div
          className={classes.sheetTop}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={(event) => {
            if (event.pointerType !== 'touch') finishDrag();
          }}
          onPointerCancel={(event) => {
            if (event.pointerType !== 'touch') resetDrag();
          }}
        >
          <div className={classes.sheetHandle} aria-hidden="true" />
          <Drawer.Header className={classes.sheetHeader}>
            <Drawer.Title className={classes.sheetTitle}>{title}</Drawer.Title>
            <Drawer.CloseButton className={classes.sheetClose} aria-label={closeLabel} />
          </Drawer.Header>
        </div>
        <Drawer.Body ref={bodyRef} className={classes.sheetBody}>
          {children}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};
